import React, { useState, useEffect } from 'react';
import { Star, BadgeCheck, Mail, Phone, Linkedin, CheckCircle2, Pencil } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';
import { CATEGORY_LABELS } from './PartnerCard';
import PartnerAvatar from './PartnerAvatar';

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-[14px] h-[14px] ${
              star <= Math.floor(rating || 0)
                ? 'text-[hsl(var(--succeedo-orange))] fill-[hsl(var(--succeedo-orange))]'
                : 'text-muted-foreground/20'
            }`}
          />
        ))}
      </div>
      {rating > 0 && (
        <span className="font-mono text-[11px] text-muted-foreground">{rating}</span>
      )}
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <>
      <div className="font-mono text-[11px] uppercase text-muted-foreground/40 tracking-wide mb-3">
        {label}
      </div>
      <div className="h-px bg-border mb-4" />
    </>
  );
}

// Picks the primary CTA label, action, and disabled state based on the intro
// state between the current viewer and this partner. Implements the gate:
// only `accepted` permits adding to the deal team; `declined` permanently
// blocks any further interaction. `cancelled`/`expired` are re-requestable.
function ctaForIntroState({ introState, onMyTeam, onRequestIntroduction, onAddToDealTeam, introLoading, addLoading }) {
  // Already a seat on the seller's deal team — short-circuit before checking
  // intro state. We surface a link to the team page so the seller has a
  // clear next step instead of a dead-end disabled button.
  if (onMyTeam) {
    return {
      label: '✓ On your deal team',
      action: () => { window.location.assign('/succeedo/teams'); },
      disabled: false,
      variant: 'subtle',
      helper: 'View them on My Deal Team →',
    };
  }

  switch (introState) {
    case 'accepted':
      return {
        label: addLoading ? 'Adding to deal team…' : 'Add to Deal Team',
        action: onAddToDealTeam,
        disabled: addLoading,
        variant: 'primary',
        helper: null,
      };
    case 'pending':
    case 'matched':
      return {
        label: 'Awaiting their response',
        action: null,
        disabled: true,
        variant: 'pending',
        helper: 'You’ve requested an introduction. We’ll let you know when they respond.',
      };
    case 'declined':
      return {
        label: 'Introduction declined',
        action: null,
        disabled: true,
        variant: 'declined',
        helper: 'This partner declined your introduction request.',
      };
    case 'completed':
      return {
        label: 'Introduction completed',
        action: null,
        disabled: true,
        variant: 'pending',
        helper: 'This introduction has been marked completed.',
      };
    case 'none':
    case 'cancelled':
    case 'expired':
    default:
      return {
        label: introLoading ? 'Requesting…' : 'Request Introduction',
        action: onRequestIntroduction,
        disabled: introLoading,
        variant: 'primary',
        helper: null,
      };
  }
}

export default function PartnerProfilePage({
  partner,
  loading = false,
  persona = 'SELLER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  introState = 'none',
  onMyTeam = false,
  viewingSelf = false,
  onRequestIntroduction,
  onAddToDealTeam,
  introLoading = false,
  addLoading = false,
  addError = null,
}) {
  const [comingSoon, setComingSoon] = useState(false);

  useEffect(() => {
    if (!comingSoon) return;
    const timer = setTimeout(() => setComingSoon(false), 4000);
    return () => clearTimeout(timer);
  }, [comingSoon]);

  if (loading) {
    return (
      <AppLayout breadcrumb="PARTNER DIRECTORY" persona={persona} userName={userName} userInitials={userInitials} planProgress={planProgress} actionPlanBadge={actionPlanBadge}>
        <div className="max-w-7xl mx-auto px-8 py-12 text-center text-muted-foreground">Loading partner...</div>
      </AppLayout>
    );
  }

  if (!partner) {
    return (
      <AppLayout breadcrumb="PARTNER DIRECTORY" persona={persona} userName={userName} userInitials={userInitials} planProgress={planProgress} actionPlanBadge={actionPlanBadge}>
        <div className="max-w-7xl mx-auto px-8 py-12 text-center">
          <p className="text-muted-foreground mb-4">Partner not found.</p>
          <a href="/succeedo/partners" className="text-[hsl(var(--succeedo-orange))] hover:underline">&larr; Back to directory</a>
        </div>
      </AppLayout>
    );
  }

  const initials = (partner.name || '')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const categoryLabel = CATEGORY_LABELS[partner.partner_category] || partner.partner_category?.toUpperCase();
  const regions = partner.geography_regions || [];
  const industries = partner.industry_tags || [];
  const safeWebsite = partner.website_url && /^https?:\/\//i.test(partner.website_url) ? partner.website_url : null;

  return (
    <AppLayout
      breadcrumb={`PARTNER DIRECTORY / ${(partner.name || '').toUpperCase()}, ${categoryLabel || ''}`}
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Breadcrumb divider */}
        <div className="mb-6">
          <div className="h-px bg-border" />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left column — Profile card */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-[16px] p-6 sticky top-24">
              {/* Avatar */}
              <PartnerAvatar
                url={partner.avatar_url}
                name={partner.name}
                initials={initials}
                sizeClass="w-20 h-20"
                textSizeClass="text-[24px]"
                className="mb-4 mx-auto"
              />

              {/* Name */}
              <h2 className="font-serif font-light text-[28px] text-foreground text-center mb-2">
                {partner.name}
              </h2>

              {/* Category badge + verified */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {categoryLabel && (
                  <div className="inline-flex items-center font-mono text-[11px] uppercase rounded-full px-3 py-1" style={{ color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.3)' }}>
                    {categoryLabel}
                  </div>
                )}
                {partner.verified && (
                  <BadgeCheck className="w-[14px] h-[14px] text-[hsl(var(--succeedo-orange))]" />
                )}
              </div>

              {/* Rating */}
              {partner.rating > 0 && (
                <div className="flex justify-center mb-6">
                  <StarRating rating={partner.rating} />
                </div>
              )}

              {/* Self-view: this partner is looking at their own public
                  profile, so swap the request/add CTAs (which are aimed
                  at sellers) for an edit affordance pointing at the
                  post-signup editor. */}
              {viewingSelf ? (
                <a
                  href="/succeedo/partner/profile/edit"
                  className="w-full rounded-full px-6 py-3 text-sm font-medium transition-colors mb-2 flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'hsl(var(--succeedo-orange))', color: 'white' }}
                >
                  <Pencil className="w-4 h-4" />
                  Edit your profile
                </a>
              ) : (() => {
                const cta = ctaForIntroState({
                  introState, onMyTeam, onRequestIntroduction, onAddToDealTeam, introLoading, addLoading,
                });
                const baseClass = 'w-full rounded-full px-6 py-3 text-sm font-medium transition-colors mb-2';
                if (cta.variant === 'primary') {
                  return (
                    <>
                      <button
                        type="button"
                        onClick={cta.action}
                        disabled={cta.disabled}
                        className={`${baseClass} disabled:opacity-50`}
                        style={{ backgroundColor: 'hsl(var(--succeedo-orange))', color: 'white' }}
                      >
                        {cta.label}
                      </button>
                      {addError && (
                        <p className="text-[12px] text-destructive text-center mb-2">{addError}</p>
                      )}
                    </>
                  );
                }
                if (cta.variant === 'subtle') {
                  return (
                    <>
                      <button
                        type="button"
                        onClick={cta.action}
                        className={`${baseClass} border-2 border-[hsl(var(--chart-2))]/30 bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] hover:bg-[hsl(var(--chart-2))]/15`}
                      >
                        {cta.label}
                      </button>
                      {cta.helper && (
                        <a
                          href="/succeedo/teams"
                          className="block text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline text-center mb-2"
                        >
                          {cta.helper}
                        </a>
                      )}
                    </>
                  );
                }
                return (
                  <>
                    <button
                      type="button"
                      disabled
                      className={`${baseClass} border border-border text-muted-foreground/40 cursor-not-allowed`}
                    >
                      {cta.label}
                    </button>
                    {cta.helper && (
                      <p className="text-[12px] text-muted-foreground text-center mb-2">{cta.helper}</p>
                    )}
                  </>
                );
              })()}

              {/* Contact info */}
              <div className="space-y-3 pt-6 border-t border-border">
                {partner.contact_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground/40" />
                    <a href={`mailto:${partner.contact_email}`} className="text-[14px] text-muted-foreground hover:text-foreground">
                      {partner.contact_email}
                    </a>
                  </div>
                )}
                {safeWebsite && (
                  <div className="flex items-center gap-3">
                    <Linkedin className="w-4 h-4 text-muted-foreground/40" />
                    <a href={safeWebsite} target="_blank" rel="noopener noreferrer" className="text-[14px] text-muted-foreground hover:text-foreground">
                      {safeWebsite.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column — Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bio */}
            {partner.bio && (
              <div>
                <SectionHeader label="ABOUT" />
                <p className="text-[16px] text-foreground/80 leading-relaxed">{partner.bio}</p>
              </div>
            )}

            {/* Firm */}
            {partner.org_name && (
              <div>
                <SectionHeader label="FIRM" />
                <p className="text-[16px] text-foreground/80">{partner.org_name}</p>
              </div>
            )}

            {/* Regions */}
            {regions.length > 0 && (
              <div>
                <SectionHeader label="REGION" />
                <div className="flex flex-wrap gap-2">
                  {regions.map((r) => (
                    <div key={r} className="font-mono text-[11px] uppercase rounded-full border border-border text-muted-foreground px-3 py-1">
                      {r}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Industries */}
            {industries.length > 0 && (
              <div>
                <SectionHeader label="INDUSTRIES" />
                <div className="flex flex-wrap gap-2">
                  {industries.map((i) => (
                    <div key={i} className="font-mono text-[11px] uppercase rounded-full border border-border text-muted-foreground px-3 py-1">
                      {i}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Back link */}
        <div className="mt-12 text-center">
          <a href="/succeedo/partners" className="text-[14px] text-muted-foreground hover:text-foreground transition-colors">
            &larr; Back to Partner Directory
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
